import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, SubHeader } from 'vetrf-ui';
import { GMO_REGISTERED_STATUS_OPTION, REGISTERED_STATUS_OPTION } from 'utils/consts/consts';
import { EntryType, ProductType } from 'components/registry/types/api';
import { CheckDataLoading } from 'components/CheckDataLoading/CheckDataLoading';
import { RemoveModal } from 'components/RemoveModal';
import { Timeline } from 'components/Timeline/Timeline';
import { RegistryOperationDictOption } from 'api/dict/params';
import { RequestTracingItemDTO } from 'api/requestMotion/types';
import { EntryRegistrationDTO } from 'api/entryRegistration/types';
import { dictApi } from 'api/dict';
import { requestMotionApi } from 'api/requestMotion';
import { AddMotionModal } from './components/AddMotionModal';
import { DocumentFields } from './components/DocumentFields';
import { FeedFormFields } from './components/FeedFormFields';
import { RequestMotionItem } from './components/RequestMotionItem/RequestMotionItem';

type RequestMotionProps = {
  id: string;
  productType: ProductType;
  entryType: EntryType;
  history: RequestTracingItemDTO[];
  updateData: () => void;
  access: boolean;
  regData?: EntryRegistrationDTO;
  isRegNumberGmoExist?: boolean;
};

export const RequestMotion = ({
  id,
  history,
  updateData,
  regData,
  access,
  entryType,
  productType,
  isRegNumberGmoExist,
}: RequestMotionProps) => {
  const [registryOperations, setRegistryOperations] = useState<RegistryOperationDictOption[]>([]);
  const [removeItemId, setRemoveItemId] = useState<string>('');
  const [isAddMotionModalOpen, setIsAddMotionModalOpen] = useState(false);
  const [isRemoveMotionModalOpen, setIsRemoveMotionModalOpen] = useState(false);
  const [error, setError] = useState('');
  const isGMO = useMemo(
    () => productType === ProductType.gmo || productType === ProductType.gmProduct,
    [productType]
  );
  const navigate = useNavigate();

  const getRegistryOperationsOptions = async () => {
    setError('');
    await dictApi
      .getRegistryOperations(productType, entryType)
      .then((res) => setRegistryOperations(res))
      .catch((e) => setError(e.response.data.message));
  };

  useEffect(() => {
    getRegistryOperationsOptions();
  }, []);

  const closeAddModal = () => {
    setIsAddMotionModalOpen(false);
  };

  const openAddModal = () => {
    setIsAddMotionModalOpen(true);
  };

  const closeRemoveModal = () => {
    setRemoveItemId('');
    setIsRemoveMotionModalOpen(false);
  };

  const openRemoveModal = (item: RequestTracingItemDTO) => {
    setRemoveItemId(item.id!);
    setIsRemoveMotionModalOpen(true);
  };

  const onAddRequestMotion = async (data: RequestTracingItemDTO) => {
    setError('');
    requestMotionApi
      .addRequestMotion(data, id)
      .then(() => {
        if (
          entryType === EntryType.requests &&
          (data.registryOperation?.registryStatus?.code === REGISTERED_STATUS_OPTION.code ||
            (isGMO &&
              data.registryOperation?.registryStatus?.code === GMO_REGISTERED_STATUS_OPTION.code))
        ) {
          navigate(`/registry/${productType}/registry/${id}`);
        } else {
          updateData();
        }
      })
      .catch((e) => setError(e.response.data.message))
      .finally(closeAddModal);
  };

  const onRemoveRequestMotion = async () => {
    setError('');
    requestMotionApi
      .removeRequestMotion(removeItemId)
      .then(updateData)
      .catch((e) => setError(e.response.data.message))
      .finally(closeRemoveModal);
  };

  return (
    <>
      <SubHeader
        header="Движения заявки"
        icon="exchange"
        iconColor="orange"
        iconSize={100}
        level={4}
        toolbar={
          access && (
            <Button color="info" size="minier" text="Добавить" icon="plus" onClick={openAddModal} />
          )
        }
      />
      <div className="mt-15">
        {error && (
          <Alert closable messageType="danger">
            {error}
          </Alert>
        )}

        <CheckDataLoading condition={!!history}>
          <Timeline
            dateField="addDate"
            nameField="registryOperation.name"
            defaultNameField="registryStatus.name"
            data={history}
            onRemove={openRemoveModal}
            allowRemove={access}
            render={(item) => !!item && <RequestMotionItem item={item} />}
          />
        </CheckDataLoading>

        {isAddMotionModalOpen && (
          <AddMotionModal
            onClose={closeAddModal}
            onSubmit={onAddRequestMotion}
            operationOptions={registryOperations}
            operationFieldName="registryOperation"
            dateFieldName="addDate"
            extraForm={
              productType === ProductType.feed ? (
                <FeedFormFields
                  regData={regData!}
                  entryType={entryType}
                  isRegNumberGmoExist={isRegNumberGmoExist!}
                />
              ) : (
                <DocumentFields />
              )
            }
          />
        )}

        {isRemoveMotionModalOpen && (
          <RemoveModal
            modalContent="Вы уверены, что хотите удалить движение заявки?"
            onClose={closeRemoveModal}
            onRemove={onRemoveRequestMotion}
          />
        )}
      </div>
    </>
  );
};
